import React, { useState } from 'react';
import { Tabs, Card, Row, Col, Statistic, Button, Typography, Space } from 'antd';
import { CalendarOutlined, CarOutlined, ReloadOutlined } from '@ant-design/icons';
import { useDashboard } from './hooks/useDashboard';
import { useLeaveApproval } from './hooks/useLeaveApproval';
import { useTripApproval } from './hooks/useTripApproval';
import LeaveGrid from './components/LeaveGrid';
import TripGrid from './components/TripGrid';
import ApproveModal from './components/ApproveModal';
import { ApproveAction } from '../../services/p9513Api';

const { Title } = Typography;

type ModalTarget = 'leave' | 'trip' | null;

const P9513Form: React.FC = () => {
  const { loading: dashLoading, dashboard, fetchDashboard } = useDashboard();
  const leaveCtx = useLeaveApproval();
  const tripCtx = useTripApproval();

  const [modalTarget, setModalTarget] = useState<ModalTarget>(null);
  const [activeTab, setActiveTab] = useState('dashboard');

  // ============================================================
  // Approve modal handlers
  // ============================================================
  const handleApproveLeaves = () => setModalTarget('leave');
  const handleApproveTrips = () => setModalTarget('trip');

  const handleModalConfirm = async (action: ApproveAction, remarks: string) => {
    if (modalTarget === 'leave') {
      await leaveCtx.batchApprove(action, remarks);
    } else if (modalTarget === 'trip') {
      await tripCtx.batchApprove(action, remarks);
    }
    setModalTarget(null);
    fetchDashboard(); // refresh counts
  };

  const handleModalCancel = () => setModalTarget(null);

  // ============================================================
  // Tab navigation from dashboard buttons
  // ============================================================
  const goToLeaveTab = () => {
    setActiveTab('leaves');
    leaveCtx.fetchLeaves();
  };

  const goToTripTab = () => {
    setActiveTab('trips');
    tripCtx.fetchTrips();
  };

  // ============================================================
  // Tab items
  // ============================================================
  const tabItems = [
    {
      key: 'dashboard',
      label: '總覽',
      children: (
        <DashboardPanel
          leaveCount={dashboard.pending_leave_count}
          tripCount={dashboard.pending_trip_count}
          loading={dashLoading}
          onRefresh={fetchDashboard}
          onGoLeave={goToLeaveTab}
          onGoTrip={goToTripTab}
        />
      ),
    },
    {
      key: 'leaves',
      label: `請假單核准${leaveCtx.total > 0 ? ` (${leaveCtx.total})` : ''}`,
      children: (
        <LeaveGrid
          leaves={leaveCtx.leaves}
          total={leaveCtx.total}
          loading={leaveCtx.loading}
          approving={leaveCtx.approving}
          selectedKeys={leaveCtx.selectedKeys}
          filter={leaveCtx.filter}
          onFilterChange={leaveCtx.setFilter}
          onSearch={leaveCtx.fetchLeaves}
          onSelect={leaveCtx.setSelectedKeys}
          onApprove={handleApproveLeaves}
        />
      ),
    },
    {
      key: 'trips',
      label: `出差單核准${tripCtx.total > 0 ? ` (${tripCtx.total})` : ''}`,
      children: (
        <TripGrid
          trips={tripCtx.trips}
          total={tripCtx.total}
          loading={tripCtx.loading}
          approving={tripCtx.approving}
          selectedKeys={tripCtx.selectedKeys}
          filter={tripCtx.filter}
          onFilterChange={tripCtx.setFilter}
          onSearch={tripCtx.fetchTrips}
          onSelect={tripCtx.setSelectedKeys}
          onApprove={handleApproveTrips}
        />
      ),
    },
  ];

  // ============================================================
  // Render
  // ============================================================
  return (
    <div style={{ padding: 16 }}>
      <Title level={4} style={{ marginBottom: 16 }}>
        考勤核准作業（副總）pw9513f1
      </Title>

      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        items={tabItems}
        destroyInactiveTabPane={false}
      />

      <ApproveModal
        open={modalTarget !== null}
        type={modalTarget ?? 'leave'}
        count={modalTarget === 'leave' ? leaveCtx.selectedKeys.length : tripCtx.selectedKeys.length}
        onConfirm={handleModalConfirm}
        onCancel={handleModalCancel}
        loading={leaveCtx.approving || tripCtx.approving}
      />
    </div>
  );
};

// ============================================================
// Dashboard sub-panel (extracted to keep P9513Form ≤ 400 lines)
// ============================================================

interface DashboardPanelProps {
  leaveCount: number;
  tripCount: number;
  loading: boolean;
  onRefresh: () => void;
  onGoLeave: () => void;
  onGoTrip: () => void;
}

const DashboardPanel: React.FC<DashboardPanelProps> = ({
  leaveCount, tripCount, loading, onRefresh, onGoLeave, onGoTrip,
}) => (
  <div>
    <Space style={{ marginBottom: 16 }}>
      <Button icon={<ReloadOutlined />} onClick={onRefresh} loading={loading} size="small">
        重新整理
      </Button>
    </Space>
    <Row gutter={[16, 16]}>
      <Col xs={24} sm={12}>
        <Card hoverable onClick={onGoLeave} style={{ textAlign: 'center', borderColor: '#1677ff' }}>
          <Statistic
            title="待審請假單"
            value={leaveCount}
            prefix={<CalendarOutlined style={{ color: '#1677ff' }} />}
            suffix="筆"
            valueStyle={{ color: leaveCount > 0 ? '#cf1322' : '#389e0d' }}
          />
          <Button type="link" style={{ marginTop: 8 }}>
            進入請假單核准
          </Button>
        </Card>
      </Col>
      <Col xs={24} sm={12}>
        <Card hoverable onClick={onGoTrip} style={{ textAlign: 'center', borderColor: '#722ed1' }}>
          <Statistic
            title="待審出差單"
            value={tripCount}
            prefix={<CarOutlined style={{ color: '#722ed1' }} />}
            suffix="筆"
            valueStyle={{ color: tripCount > 0 ? '#cf1322' : '#389e0d' }}
          />
          <Button type="link" style={{ marginTop: 8 }}>
            進入出差單核准
          </Button>
        </Card>
      </Col>
    </Row>
  </div>
);

export default P9513Form;
